import React, { useEffect, useMemo, useRef, useState } from 'react';
import './AppointmentDateTimeRangeField.css';

const monthFormatter = new Intl.DateTimeFormat('nb-NO', {
  month: 'long',
  year: 'numeric',
});

const weekdayFormatter = new Intl.DateTimeFormat('nb-NO', {
  weekday: 'short',
});

const selectedDateFormatter = new Intl.DateTimeFormat('nb-NO', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const ariaDateFormatter = new Intl.DateTimeFormat('nb-NO', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const pad = (value) => String(value).padStart(2, '0');

const splitDateTimeInputValue = (value) => {
  const [date = '', time = ''] = String(value || '').split('T');
  return {
    date: /^\d{4}-\d{2}-\d{2}$/u.test(date) ? date : '',
    time: /^\d{2}:\d{2}/u.test(time) ? time.slice(0, 5) : '',
  };
};

const composeDateTimeInputValue = (date, time) => (
  date && time ? `${date}T${time}` : ''
);

const getLocalDateKey = (date) => (
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
);

const parseDateKey = (dateKey) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(dateKey || '');
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

const getMonthStart = (value) => {
  const source = value instanceof Date ? new Date(value) : new Date();
  source.setDate(1);
  source.setHours(0, 0, 0, 0);
  return source;
};

const addMonths = (date, months) => {
  const copy = getMonthStart(date);
  copy.setMonth(copy.getMonth() + months);
  return copy;
};

const addDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const getCalendarDays = (monthStart) => {
  const first = getMonthStart(monthStart);
  const firstDay = first.getDay();
  const gridStart = addDays(first, firstDay === 0 ? -6 : 1 - firstDay);
  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
};

const formatFieldValue = ({ selectedDate, startTime, endTime }) => {
  if (!selectedDate || !startTime) return 'Velg dato og tid';
  const date = parseDateKey(selectedDate);
  if (!date) return 'Velg dato og tid';
  return `${selectedDateFormatter.format(date)}, ${startTime}${endTime ? ` - ${endTime}` : ''}`;
};

const CalendarIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <path d="M3 10h18" />
    <rect x="3" y="4" width="18" height="18" rx="2" />
  </svg>
);

const AppointmentDateTimeRangeField = ({
  disabled = false,
  endValue = '',
  onChange,
  startValue = '',
  className = '',
}) => {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const start = splitDateTimeInputValue(startValue);
  const end = splitDateTimeInputValue(endValue);
  const selectedDate = start.date || end.date || '';
  const startTime = start.time;
  const endTime = end.time;
  const [viewMonth, setViewMonth] = useState(() => getMonthStart(parseDateKey(selectedDate) || new Date()));
  const calendarDays = useMemo(() => getCalendarDays(viewMonth), [viewMonth]);
  const todayKey = getLocalDateKey(new Date());

  useEffect(() => {
    if (open) {
      setViewMonth(getMonthStart(parseDateKey(selectedDate) || new Date()));
    }
  }, [open, selectedDate]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current || rootRef.current.contains(event.target)) return;
      setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const emitChange = (nextDate, nextStartTime, nextEndTime) => {
    onChange({
      plannedStartAt: composeDateTimeInputValue(nextDate, nextStartTime),
      plannedEndAt: composeDateTimeInputValue(nextDate, nextEndTime),
    });
  };

  const handleSelectDate = (dateKey) => {
    emitChange(dateKey, startTime || '09:00', endTime);
  };

  const handleStartTimeChange = (event) => {
    const nextStartTime = event.target.value;
    emitChange(selectedDate, nextStartTime, endTime);
  };

  const handleEndTimeChange = (event) => {
    const nextEndTime = event.target.value;
    emitChange(selectedDate, startTime, nextEndTime);
  };

  const displayValue = formatFieldValue({ selectedDate, startTime, endTime });

  return (
    <div ref={rootRef} className={`appointment-date-time-field${open ? ' is-open' : ''}${className ? ` ${className}` : ''}`}>
      <span className="appointment-date-time-heading">Dato</span>
      <button
        type="button"
        className={`appointment-date-time-trigger${selectedDate && startTime ? ' has-value' : ''}`}
        disabled={disabled}
        aria-label="Dato"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <CalendarIcon />
        <span>{displayValue}</span>
      </button>

      {open ? (
        <div className="appointment-date-time-dialog" role="dialog" aria-label="Velg dato og tid">
          <header className="appointment-date-time-dialog-header">
            <button
              type="button"
              className="appointment-date-time-nav"
              onClick={() => setViewMonth((current) => addMonths(current, -1))}
              aria-label="Forrige måned"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <strong>{monthFormatter.format(viewMonth)}</strong>
            <button
              type="button"
              className="appointment-date-time-nav"
              onClick={() => setViewMonth((current) => addMonths(current, 1))}
              aria-label="Neste måned"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </header>

          <div className="appointment-date-time-weekdays" aria-hidden="true">
            {calendarDays.slice(0, 7).map((date) => (
              <span key={getLocalDateKey(date)}>{weekdayFormatter.format(date)}</span>
            ))}
          </div>
          <div className="appointment-date-time-calendar">
            {calendarDays.map((date) => {
              const dateKey = getLocalDateKey(date);
              const isSelected = dateKey === selectedDate;
              const isToday = dateKey === todayKey;
              const isOutsideMonth = date.getMonth() !== viewMonth.getMonth();
              return (
                <button
                  key={dateKey}
                  type="button"
                  className={[
                    'appointment-date-time-day',
                    isSelected ? 'is-selected' : '',
                    isToday ? 'is-today' : '',
                    isOutsideMonth ? 'is-outside-month' : '',
                  ].filter(Boolean).join(' ')}
                  aria-label={ariaDateFormatter.format(date)}
                  aria-pressed={isSelected}
                  onClick={() => handleSelectDate(dateKey)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="appointment-date-time-time-section">
            <label>
              <span>Fra</span>
              <input
                className="input"
                type="time"
                value={startTime}
                disabled={!selectedDate}
                onChange={handleStartTimeChange}
              />
            </label>
          </div>

          <div className={`appointment-date-time-end-section${endTime ? '' : ' is-empty'}`}>
            <label>
              <span>
                Til
                <em>valgfritt</em>
              </span>
              <input
                className="input"
                type="time"
                aria-label="Til (valgfritt)"
                value={endTime}
                disabled={!selectedDate}
                onChange={handleEndTimeChange}
              />
            </label>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AppointmentDateTimeRangeField;
